/**
 * LLM-backed metric advisor.
 *
 * Implements the same swappable `MetricAdvisor` seam as `heuristicAdvisor`, but
 * calls the real Metric Inference API (Spec 2, `POST /api/onboarding/infer-metrics`)
 * instead of client-side keyword rules. Built as a factory so the call site can
 * bind the extra fields the backend needs (toolName/category/schema/tableName)
 * that aren't part of the LOCKED `MetricSuggestionInput`.
 *
 * Unlike `metricAdvisor.ts` (which is deliberately network-free), this module
 * depends on the API client.
 */

import {
  BaseMetricDraft,
  ColumnInfo,
  ColumnSamplePayload,
  DerivedMetricDraft,
  InferMetricsRequest,
  InferMetricsResponse,
  MetricAdvisor,
  MetricSuggestion,
  MetricSuggestionInput,
} from './types';
import { submitInferMetrics } from './onboardingApi';
import { pollInferJob } from './pollInferJob';

/** Backend caps `business_context` at 4000 chars; truncate defensively. */
const BUSINESS_CONTEXT_MAX = 4000;

/** Dependencies the LLM advisor needs beyond MetricSuggestionInput. */
export interface LlmMetricAdvisorDeps {
  toolName: string;
  category: string;
  schema: string | null;
  tableName: string;
  /**
   * Injectable end-to-end inference fn (submit → poll → response). Defaults to
   * the composed submitInferMetrics + pollInferJob. Existing tests inject this
   * to return an InferMetricsResponse directly, bypassing the async transport.
   */
  inferMetricsFn?: (req: InferMetricsRequest) => Promise<InferMetricsResponse>;
  /** Injectable submit client (Spec 3.5 async); defaults to submitInferMetrics. */
  submitFn?: typeof submitInferMetrics;
  /** Injectable poll loop (Spec 3.5 async); defaults to pollInferJob. */
  pollFn?: typeof pollInferJob;
}

/**
 * Folds the wizard-only per-column notes into the businessContext string, since
 * the backend contract has no place for columnNotes. Returns undefined when
 * there's nothing to send (so the optional field stays null server-side), and
 * truncates to the backend's 4000-char cap.
 */
export function buildBusinessContext(
  businessContext: string,
  columnNotes: Record<string, string>,
): string | undefined {
  const noteLines = Object.entries(columnNotes)
    .filter(([, note]) => note && note.trim().length > 0)
    .map(([column, note]) => `- ${column}: ${note.trim()}`);

  const parts: string[] = [];
  if (businessContext && businessContext.trim().length > 0) {
    parts.push(businessContext.trim());
  }
  if (noteLines.length > 0) {
    parts.push(`Column notes:\n${noteLines.join('\n')}`);
  }
  if (parts.length === 0) return undefined;

  const combined = parts.join('\n\n');
  return combined.length > BUSINESS_CONTEXT_MAX
    ? combined.slice(0, BUSINESS_CONTEXT_MAX)
    : combined;
}

/** Maps the wizard's columns to the backend ColumnSample payload (drops ordinalPosition). */
export function toColumnSamples(columns: ColumnInfo[]): ColumnSamplePayload[] {
  return columns.map((c) => ({
    columnName: c.columnName,
    dataType: c.dataType,
    isNullable: c.isNullable,
    distinctValues: c.distinctValues,
    sampleValues: c.sampleValues,
  }));
}

/**
 * Maps the LLM response to a MetricSuggestion. `rationale` has no home on
 * BaseMetricDraft (the contract is locked), so non-null rationales are appended
 * to `warnings` as "metricKey: rationale" — surfacing the "why" in the existing
 * warning Alert rather than dropping it silently.
 */
function toSuggestion(response: InferMetricsResponse): MetricSuggestion {
  const baseMetrics: BaseMetricDraft[] = response.baseMetrics.map((m) => ({
    metricKey: m.metricKey,
    exprSql: m.exprSql,
    agg: m.agg,
    filterCondition: m.filterCondition,
    // Carry the backend's publish decision through so a dependency-only helper
    // base isn't re-exposed as a standalone metric by passthrough synthesis. The
    // backend defaults publish to true; preserve `undefined` as-is (also treated
    // as exposed) rather than coercing, so an older backend without the field
    // behaves identically to today.
    publish: m.publish,
  }));

  const derivedMetrics: DerivedMetricDraft[] = response.derivedMetrics.map((m) => ({
    derivedMetricKey: m.derivedMetricKey,
    baseMetricsCsv: m.baseMetricsCsv,
    exprSql: m.exprSql,
    description: m.description,
  }));

  const rationaleWarnings = response.baseMetrics
    .filter((m) => m.rationale && m.rationale.trim().length > 0)
    .map((m) => `${m.metricKey}: ${m.rationale}`);

  return {
    baseMetrics,
    derivedMetrics,
    warnings: [...response.warnings, ...rationaleWarnings],
  };
}

/**
 * Builds a MetricAdvisor that proposes metrics via the Spec 2 LLM endpoint.
 * Bind it at the call site with the table/tool context, then pass it into
 * MetricsStep's `advisor` prop.
 */
export function createLlmMetricAdvisor(deps: LlmMetricAdvisorDeps): MetricAdvisor {
  const submit = deps.submitFn ?? submitInferMetrics;
  const poll = deps.pollFn ?? pollInferJob;
  // Default: submit an async job, poll until COMPLETED, cast the free-form
  // result to the metrics response shape. Tests may inject `inferMetricsFn` to
  // short-circuit this transport while keeping the request/response mapping.
  const callInfer =
    deps.inferMetricsFn ??
    (async (req: InferMetricsRequest): Promise<InferMetricsResponse> => {
      const { jobId } = await submit(req);
      const result = await poll(jobId);
      return result as InferMetricsResponse;
    });
  return {
    async suggest(input: MetricSuggestionInput): Promise<MetricSuggestion> {
      const response = await callInfer({
        toolName: deps.toolName,
        category: deps.category,
        sourceTable: {
          schema: deps.schema,
          tableName: deps.tableName,
          columns: toColumnSamples(input.columns),
        },
        businessContext: buildBusinessContext(input.businessContext, input.columnNotes),
      });
      return toSuggestion(response);
    },
  };
}
